/**
 * Utility functions for generating PDF export options for TinyMCE
 * strictly following APA 7th Edition guidelines and institutional overrides.
 */

/**
 * Generates an abbreviated running head from the title (max 50 chars).
 * Rules:
 * - All CAPS
 * - Max 50 chars counting spaces/punctuation
 * - If title <= 50, use full title
 * - If title > 50, truncate to nearest word <= 50
 * - No ellipsis
 * 
 * @param {string} title - The document title
 * @returns {string} - The formatted running head
 */
export const getRunningHead = (title) => {
    if (!title) return 'UNTITLED';

    const upperTitle = title.toUpperCase();

    if (upperTitle.length <= 50) {
        return upperTitle;
    }

    // Truncate to 50 chars
    let truncated = upperTitle.slice(0, 50);

    // If we cut in the middle of a word, find the last space
    // Check if the 51st char was a space (meaning we cut exactly at a word end)
    // or if the current end is not a space
    if (upperTitle[50] !== ' ' && truncated.lastIndexOf(' ') > -1) {
        truncated = truncated.slice(0, truncated.lastIndexOf(' '));
    }

    // Trim any trailing punctuation or spaces
    return truncated.replace(/[.,;:\s]+$/, '');
};

/**
 * Generates the HTML for the PDF header based on document type.
 * 
 * @param {object} doc - The document object
 * @param {string} documentTypeName - The name of the document type (e.g. "Dissertation", "Academic Paper")
 * @returns {string} - HTML string for the header
 */
const getHeaderHtml = (title, documentTypeName) => {
    const type = documentTypeName?.toLowerCase() || '';
    const runningHead = getRunningHead(title);

    // CSS styles for the header content
    // using a table structure is more robust for various PDF engines than flexbox
    const containerStyle = 'width: calc(100% - 2in); margin-left: 1in; margin-right: 1in; margin-top: 0.5in; font-family: "Times New Roman", Times, serif; font-size: 12pt; border: none; border-collapse: collapse; margin-bottom: 0;';
    const runningHeadCellStyle = 'text-align: left; text-transform: uppercase; vertical-align: top; width: 85%;';
    const pageNumberCellStyle = 'text-align: right; vertical-align: top; width: 15%;';

    // --- Logic based on Document Type ---

    // 1. Professional Paper / Dissertation / Thesis
    // Format: Running Head (LEFT) + Page Number (RIGHT)
    if (
        type.includes('dissertation') ||
        type.includes('thesis') ||
        (type.includes('academic') && type.includes('professional'))
    ) {
        return `
            <table style="${containerStyle}">
                <tr>
                    <td style="${runningHeadCellStyle}">${runningHead}</td>
                    <td style="${pageNumberCellStyle}"><span class="pageNumber"></span></td>
                </tr>
            </table>
        `;
    }

    // 2. Student Paper / Honor's Thesis (Default)
    // Format: Page Number (RIGHT) only
    return `
        <table style="${containerStyle}">
                <tr>
                    <td style="${runningHeadCellStyle}"></td>
                    <td style="${pageNumberCellStyle}"><span class="pageNumber"></span></td>
                </tr>
            </table>
    `;
};

/**
 * Generates the HTML for the PDF footer.
 * Currently mostly empty or specific page numbers for front matter if needed in future.
 * 
 * @param {object} doc 
 * @param {string} documentTypeName 
 * @returns {string|null} 
 */
const getFooterHtml = (title, documentTypeName) => {
    // Current requirements don't strictly specify a footer for the main body 
    // export except for potentially specific page number overrides (like bottom center).
    // For now, we return null to let the margin remains clear or use default if needed.
    return null;

    // Example for bottom-center page number logic if implemented later:
    /*
    return `
        <div style="font-family: 'Times New Roman'; font-size: 12pt; text-align: center; width: 100%;">
            <span class="pageNumber"></span>
        </div>
    `;
    */
};

/**
 * Main function to get the full export options object for TinyMCE
 * 
 * @param {object} doc - The full document object
 * @returns {object} - The configuration object for exportpdf_converter_options
 */
export const getExportPdfOptions = (title, documentTypeName = 'Academic Paper') => {
    const headerHtml = getHeaderHtml(title, documentTypeName);
    const footerHtml = getFooterHtml(title, documentTypeName);

    const options = {
        'format': 'A4', // Standard
        'margin_top': '1in',
        'margin_right': '1in',
        'margin_bottom': '1in',
        'margin_left': '1in',

        // Header settings
        'header_html': headerHtml,
        'header_spacing': '0.5in', // Standard APA distance from top
    };

    if (footerHtml) {
        options['footer_html'] = footerHtml;
        options['footer_spacing'] = '0.5in';
    }

    return options;
};
